import React, { useState, useEffect } from 'react';
import api from '../services/api';
import logo1 from '../assets/logo1.png';
import Navbar from '../components/Navbar';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';

// ✅ Outside parent — prevents keyboard dismiss on mobile
const YearForm = ({ formData, setFormData, onSubmit, onCancel, submitLabel }) => (
    <form onSubmit={onSubmit} style={styles.inlineForm}>
        <div style={styles.formGrid}>
            <div style={styles.formGroup}>
                <label style={styles.label}>📅 Year Label</label>
                <input style={styles.input} value={formData.yearLabel}
                    onChange={e => setFormData(prev => ({...prev, yearLabel: e.target.value}))}
                    placeholder="e.g. 2026" required />
            </div>
            <div style={styles.formGroup}>
                <label style={styles.label}>📋 Term</label>
                <select style={styles.input} value={formData.term}
                    onChange={e => setFormData(prev => ({...prev, term: e.target.value}))} required>
                    <option value="">Select Term</option>
                    <option value="1">Term 1 (Jan – Apr)</option>
                    <option value="2">Term 2 (May – Aug)</option>
                    <option value="3">Term 3 (Sep – Dec)</option>
                </select>
            </div>
            <div style={styles.formGroup}>
                <label style={styles.label}>🗓️ Start Date</label>
                <input type="date" style={styles.input} value={formData.startDate}
                    onChange={e => setFormData(prev => ({...prev, startDate: e.target.value}))} required />
            </div>
            <div style={styles.formGroup}>
                <label style={styles.label}>🗓️ End Date</label>
                <input type="date" style={styles.input} value={formData.endDate}
                    onChange={e => setFormData(prev => ({...prev, endDate: e.target.value}))} required />
            </div>
            <div style={styles.formGroup}>
                <label style={styles.label}>🔖 Status</label>
                <select style={styles.input} value={String(formData.isActive)}
                    onChange={e => setFormData(prev => ({...prev, isActive: e.target.value === 'true'}))}>
                    <option value="false">❌ Inactive</option>
                    <option value="true">✅ Active</option>
                </select>
            </div>
        </div>
        <div style={styles.btnGroup}>
            <button type="submit" style={styles.submitBtn}>{submitLabel}</button>
            <button type="button" onClick={onCancel} style={styles.cancelBtn}>✕ Cancel</button>
        </div>
    </form>
);

function AcademicYears() {
    const [years, setYears] = useState([]);
    const [classes, setClasses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingYear, setEditingYear] = useState(null);
    const [showPromote, setShowPromote] = useState(false);
    const [promoting, setPromoting] = useState(false);
    const [promotionProgress, setPromotionProgress] = useState(null);
    const [promotionPairs, setPromotionPairs] = useState([]);
    const [deleteConfirm, setDeleteConfirm] = useState(null);

    const [formData, setFormData] = useState({
        yearLabel: '', term: '', startDate: '', endDate: '', isActive: false
    });
    const emptyForm = { yearLabel: '', term: '', startDate: '', endDate: '', isActive: false };

    const promotionMap = {
        'PG': 'PP1', 'PP1': 'PP2', 'PP2': 'G1',
        'G1': 'G2', 'G2': 'G3', 'G3': 'G4',
        'G4': 'G5', 'G5': 'G6', 'G6': 'G7',
        'G7': 'G8', 'G8': 'G9'
    };

    useEffect(() => { fetchYears(); fetchClasses(); }, []);

    const fetchYears = async () => {
        try {
            const response = await api.get('/api/academic-years');
            setYears(response.data);
            setLoading(false);
        } catch (err) { setError('Failed to load academic years'); setLoading(false); }
    };

    const fetchClasses = async () => {
        try { const r = await api.get('/api/classes'); setClasses(r.data); } catch (err) {}
    };

    const handleEdit = (year) => {
        if (editingYear?.yearId === year.yearId) { setEditingYear(null); return; }
        setEditingYear(year);
        setFormData({ yearLabel: year.yearLabel, term: year.term, startDate: year.startDate, endDate: year.endDate, isActive: year.isActive });
        setShowAddForm(false);
    };

    const handleCancelEdit = () => { setEditingYear(null); setFormData(emptyForm); };

    const handleSubmitAdd = async (e) => {
        e.preventDefault();
        try {
            await api.post('/api/academic-years', { ...formData, term: parseInt(formData.term) });
            setShowAddForm(false); setFormData(emptyForm); fetchYears();
            setSuccessMsg('✅ Academic year added!'); setTimeout(() => setSuccessMsg(''), 3000);
        } catch (err) { setError('Failed to save academic year'); }
    };

    const handleSubmitEdit = async (e) => {
        e.preventDefault();
        try {
            await api.put(`/api/academic-years/${editingYear.yearId}`, { ...formData, term: parseInt(formData.term) });
            setEditingYear(null); setFormData(emptyForm); fetchYears();
            setSuccessMsg('✅ Academic year updated!'); setTimeout(() => setSuccessMsg(''), 3000);
        } catch (err) { setError('Failed to update academic year'); }
    };

    const handleDelete = async (id) => {
        try { await api.delete(`/api/academic-years/${id}`); fetchYears(); setDeleteConfirm(null); }
        catch (err) { setError('Failed to delete academic year'); }
    };

    const handleSetActive = async (year) => {
        try {
            await api.put(`/api/academic-years/${year.yearId}`, { ...year, isActive: !year.isActive });
            fetchYears();
            setSuccessMsg(year.isActive ? 'Year deactivated' : '✅ Year set as active!');
            setTimeout(() => setSuccessMsg(''), 2000);
        } catch (err) { setError('Failed to update status'); }
    };

    const openPromotion = () => {
        const pairs = classes.map(cls => {
            const gradeLevel = cls.gradeLevel;
            const nextGrade = promotionMap[gradeLevel];
            const streamSuffix = cls.stream ? cls.stream.charAt(0).toUpperCase() : '';
            const suggestedNext = nextGrade
                ? classes.find(c => c.gradeLevel === nextGrade && (streamSuffix ? c.stream?.charAt(0).toUpperCase() === streamSuffix : !c.stream))
                    || classes.find(c => c.gradeLevel === nextGrade)
                : null;
            return {
                fromClassId: String(cls.classId), fromClassName: cls.className, fromGrade: gradeLevel,
                toClassId: suggestedNext ? String(suggestedNext.classId) : '', toClassName: suggestedNext?.className || '',
                skip: !nextGrade
            };
        }).sort((a, b) => a.fromClassName.localeCompare(b.fromClassName));
        setPromotionPairs(pairs);
        setShowPromote(true);
        setPromotionProgress(null);
    };

    const handlePromote = async () => {
        const validPairs = promotionPairs.filter(p => !p.skip && p.toClassId);
        if (!validPairs.length) { setError('No valid promotion pairs selected'); return; }
        setPromoting(true);
        let success = 0, failed = 0;
        setPromotionProgress({ done: 0, total: validPairs.length, success: 0, failed: 0 });
        for (let i = 0; i < validPairs.length; i++) {
            const pair = validPairs[i];
            try {
                const studentsRes = await api.get('/api/students');
                const classStudents = studentsRes.data.filter(s => String(s.schoolClass?.classId) === String(pair.fromClassId));
                for (const student of classStudents) {
                    await api.put(`/api/students/${student.studentId}/move-class/${pair.toClassId}`);
                }
                success++;
            } catch (err) { failed++; }
            setPromotionProgress({ done: i + 1, total: validPairs.length, success, failed });
        }
        setPromoting(false);
        setSuccessMsg(`🎓 Promotion complete! ${success} class(es) promoted. ${failed > 0 ? `${failed} failed.` : ''}`);
        setTimeout(() => setSuccessMsg(''), 5000);
        setShowPromote(false);
        fetchClasses();
    };

    const groupedYears = years.reduce((groups, year) => {
        if (!groups[year.yearLabel]) groups[year.yearLabel] = [];
        groups[year.yearLabel].push(year);
        return groups;
    }, {});

    const activeYear = years.find(y => y.isActive);

    return (
        <div style={styles.container}>
           <Navbar />
            <div style={styles.layoutRow}>
            <Sidebar />
             <div style={styles.content}>
                <div style={styles.header}>
                    <div>
                        <h2 style={styles.title}>📅 Academic Years</h2>
                        <p style={styles.subtitle}>
                            {activeYear
                                ? <span>Active: <strong>{activeYear.yearLabel} — Term {activeYear.term}</strong> ({activeYear.startDate} → {activeYear.endDate})</span>
                                : 'No active term set'}
                        </p>
                    </div>
                    <div style={styles.headerBtns}>
                        <button onClick={openPromotion} style={styles.promoteBtn}>🎓 Promote Students</button>
                        <button onClick={() => { setShowAddForm(!showAddForm); setEditingYear(null); }} style={styles.addBtn}>
                            {showAddForm ? '✕ Cancel' : '+ Add Term'}
                        </button>
                    </div>
                </div>

                {error && <p style={styles.error}>{error}</p>}
                {successMsg && <p style={styles.success}>{successMsg}</p>}

                {showAddForm && (
                    <div style={styles.addFormCard}>
                        <h3 style={styles.formTitle}>➕ Add Academic Term</h3>
                        <YearForm formData={formData} setFormData={setFormData}
                            onSubmit={handleSubmitAdd}
                            onCancel={() => { setShowAddForm(false); setFormData(emptyForm); }}
                            submitLabel="💾 Save" />
                    </div>
                )}

                {/* Delete Confirmation Modal */}
                {deleteConfirm && (
                    <div style={styles.modalOverlay}>
                        <div style={{ backgroundColor:'white', padding:'25px 30px', borderRadius:'14px', maxWidth:'380px', width:'90%' }}>
                            <h3 style={{ color:'#dc3545', margin:'0 0 12px 0' }}>🗑️ Delete Academic Year?</h3>
                            <p style={{ color:'#555', marginBottom:'20px' }}>
                                Delete <strong>{deleteConfirm.yearLabel} — Term {deleteConfirm.term}</strong>? This cannot be undone.
                            </p>
                            <div style={{ display:'flex', gap:'10px', justifyContent:'flex-end' }}>
                                <button onClick={() => setDeleteConfirm(null)} style={styles.cancelBtn}>Cancel</button>
                                <button onClick={() => handleDelete(deleteConfirm.yearId)} style={{ ...styles.cancelBtn, backgroundColor:'#dc3545' }}>🗑️ Delete</button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Promotion Modal */}
                {showPromote && (
                    <div style={styles.modalOverlay}>
                        <div style={styles.modal}>
                            <div style={styles.modalHeader}>
                                <h2 style={styles.modalTitle}>🎓 End of Year Student Promotion</h2>
                                <button onClick={() => setShowPromote(false)} style={styles.modalClose}>✕</button>
                            </div>
                            <p style={styles.modalSubtitle}>
                                Review the promotion mapping below. Students will move to the target class keeping all their results.
                                G9 students graduate and are not promoted.
                            </p>

                            {promotionProgress && (
                                <div style={{ margin:'15px 25px 0' }}>
                                    <div style={{ height:'8px', backgroundColor:'#e9ecef', borderRadius:'4px', overflow:'hidden', marginBottom:'6px' }}>
                                        <div style={{ height:'100%', backgroundColor:'#28a745', width:`${(promotionProgress.done/promotionProgress.total)*100}%`, transition:'width 0.3s' }} />
                                    </div>
                                    <p style={{ fontSize:'12px', color:'#666', margin:0 }}>
                                        Promoting {promotionProgress.done}/{promotionProgress.total} — ✅ {promotionProgress.success} done
                                        {promotionProgress.failed > 0 && ` — ❌ ${promotionProgress.failed} failed`}
                                    </p>
                                </div>
                            )}

                            <div style={styles.promotionTable}>
                                <div style={styles.promotionHeader}>
                                    <span>From Class</span>
                                    <span style={{ textAlign:'center' }}>→</span>
                                    <span>To Class (editable)</span>
                                    <span style={{ textAlign:'center' }}>Skip?</span>
                                </div>
                                <div style={{ maxHeight:'350px', overflowY:'auto' }}>
                                    {promotionPairs.map((pair, i) => (
                                        <div key={i} style={{ display:'grid', gridTemplateColumns:'1fr 30px 1fr 80px', gap:'10px', padding:'8px 25px', alignItems:'center', borderBottom:'1px solid #eee', backgroundColor: pair.skip ? '#f8f9fa' : i%2===0?'white':'#fafafa', opacity: pair.skip?0.6:1 }}>
                                            <span style={{ backgroundColor:'#e3f2fd', color:'#1F3864', padding:'5px 10px', borderRadius:'8px', fontWeight:'bold', fontSize:'13px' }}>{pair.fromClassName}</span>
                                            <span style={{ color:'#28a745', fontWeight:'bold', fontSize:'16px', textAlign:'center' }}>→</span>
                                            <select style={{ padding:'6px 8px', borderRadius:'8px', border:'1px solid #ddd', fontSize:'12px', width:'100%' }}
                                                value={pair.toClassId} disabled={pair.skip}
                                                onChange={e => {
                                                    const newPairs = [...promotionPairs];
                                                    const selected = classes.find(c => String(c.classId) === e.target.value);
                                                    newPairs[i] = { ...newPairs[i], toClassId: e.target.value, toClassName: selected?.className || '' };
                                                    setPromotionPairs(newPairs);
                                                }}>
                                                <option value="">-- Select Target --</option>
                                                {classes.filter(c => String(c.classId) !== pair.fromClassId).map(c => (
                                                    <option key={c.classId} value={c.classId}>{c.className}</option>
                                                ))}
                                            </select>
                                            <div style={{ display:'flex', justifyContent:'center' }}>
                                                {pair.skip
                                                    ? <span style={{ backgroundColor:'#ffc107', color:'#856404', padding:'3px 8px', borderRadius:'8px', fontSize:'11px', fontWeight:'bold' }}>🎓 Graduates</span>
                                                    : <button onClick={() => { const n=[...promotionPairs]; n[i]={...n[i],skip:true}; setPromotionPairs(n); }}
                                                        style={{ backgroundColor:'#6c757d', color:'white', border:'none', padding:'3px 8px', borderRadius:'8px', cursor:'pointer', fontSize:'11px' }}>Skip</button>
                                                }
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div style={{ padding:'15px 25px', borderTop:'1px solid #eee', display:'flex', gap:'10px', justifyContent:'flex-end' }}>
                                <button onClick={handlePromote} disabled={promoting}
                                    style={{ backgroundColor:'#28a745', color:'white', border:'none', padding:'10px 25px', borderRadius:'10px', cursor:'pointer', fontWeight:'bold', fontSize:'14px' }}>
                                    {promoting ? '⏳ Promoting...' : `🎓 Confirm Promotion (${promotionPairs.filter(p => !p.skip && p.toClassId).length} classes)`}
                                </button>
                                <button onClick={() => setShowPromote(false)} style={styles.cancelBtn}>Cancel</button>
                            </div>
                        </div>
                    </div>
                )}

                {loading ? <p style={{ textAlign:'center', padding:'40px', color:'#666' }}>⏳ Loading...</p> :
                    Object.keys(groupedYears).length === 0 ? (
                        <div style={styles.emptyState}>
                            <div style={{ fontSize:'48px', marginBottom:'15px' }}>📅</div>
                            <h3>No Academic Years Yet</h3>
                            <p>Click + Add Term to get started</p>
                        </div>
                    ) : (
                        Object.entries(groupedYears).sort(([a],[b]) => b.localeCompare(a)).map(([yearLabel, termsList]) => (
                            <div key={yearLabel} style={styles.yearBlock}>
                                <div style={styles.yearHeader}>
                                    <span style={styles.yearTitle}>📅 {yearLabel}</span>
                                    <span style={{ backgroundColor:'rgba(255,255,255,0.2)', color:'white', padding:'3px 12px', borderRadius:'20px', fontSize:'12px' }}>{termsList.length} term(s)</span>
                                </div>
                                <div style={styles.termsGrid}>
                                    {termsList.sort((a,b) => a.term-b.term).map(year => (
                                        <div key={year.yearId}>
                                            <div style={{ ...styles.termCard, borderTop:`4px solid ${year.isActive?'#28a745':'#ddd'}`, outline: editingYear?.yearId === year.yearId ? '2px solid #2E75B6' : 'none' }}
                                                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.1)'; }}
                                                onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}>
                                                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px' }}>
                                                    <span style={{ fontSize:'20px', fontWeight:800, color:'#1F3864' }}>Term {year.term}</span>
                                                    <span style={{ color:'white', padding:'3px 10px', borderRadius:'20px', fontSize:'11px', fontWeight:'bold', backgroundColor: year.isActive?'#28a745':'#6c757d' }}>
                                                        {year.isActive ? '✅ Active' : 'Inactive'}
                                                    </span>
                                                </div>
                                                <div style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'12px', color:'#666', marginBottom:'12px' }}>
                                                    <span>📅 {year.startDate}</span>
                                                    <span>→</span>
                                                    <span>📅 {year.endDate}</span>
                                                </div>
                                                <div style={{ display:'flex', gap:'6px', flexWrap:'wrap' }}>
                                                    <button onClick={() => handleSetActive(year)} style={{ flex:1, backgroundColor: year.isActive?'#6c757d':'#28a745', color:'white', border:'none', padding:'7px 10px', borderRadius:'8px', cursor:'pointer', fontSize:'11px', fontWeight:'bold' }}>
                                                        {year.isActive ? 'Deactivate' : '✅ Set Active'}
                                                    </button>
                                                    <button onClick={() => handleEdit(year)} style={{ flex:1, backgroundColor: editingYear?.yearId===year.yearId?'#6c757d':'#2E75B6', color:'white', border:'none', padding:'7px 10px', borderRadius:'8px', cursor:'pointer', fontSize:'11px' }}>
                                                        {editingYear?.yearId===year.yearId ? '✕' : 'Edit'}
                                                    </button>
                                                    <button onClick={() => setDeleteConfirm(year)} style={{ backgroundColor:'#dc3545', color:'white', border:'none', padding:'7px 10px', borderRadius:'8px', cursor:'pointer', fontSize:'11px' }}>
                                                        🗑️
                                                    </button>
                                                </div>
                                            </div>
                                            {editingYear?.yearId === year.yearId && (
                                                <div style={{ backgroundColor:'white', borderRadius:'0 0 10px 10px', padding:'15px', border:'2px solid #2E75B6', borderTop:'none', marginTop:'-2px' }}>
                                                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'12px' }}>
                                                        <h4 style={{ color:'#2E75B6', margin:0, fontSize:'13px' }}>✏️ Editing: {year.yearLabel} Term {year.term}</h4>
                                                        <button onClick={handleCancelEdit} style={{ background:'none', border:'none', fontSize:'16px', cursor:'pointer', color:'#999' }}>✕</button>
                                                    </div>
                                                    <YearForm formData={formData} setFormData={setFormData}
                                                        onSubmit={handleSubmitEdit} onCancel={handleCancelEdit} submitLabel="✅ Update" />
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))
                    )
                }
                
            </div>
           
        </div>
         <Footer />
        </div>
    );
}

const styles = {
    container: { minHeight:'100vh', backgroundColor:'#f0f2f5' },
    layoutRow: { display:'flex' },
    content: { padding: '30px', flex: 1 },
    header: { display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'24px', flexWrap:'wrap', gap:'10px' },
    headerBtns: { display:'flex', gap:'10px', flexWrap:'wrap' },
    title: { color:'#1F3864', margin:'0 0 5px 0', fontSize:'24px', fontWeight:800 },
    subtitle: { color:'#888', margin:0, fontSize:'14px' },
    addBtn: { backgroundColor:'#1F3864', color:'white', border:'none', padding:'11px 22px', borderRadius:'10px', cursor:'pointer', fontWeight:'bold', fontSize:'13px' },
    promoteBtn: { backgroundColor:'#28a745', color:'white', border:'none', padding:'11px 22px', borderRadius:'10px', cursor:'pointer', fontWeight:'bold', fontSize:'13px' },
    error: { color:'#dc3545', padding:'12px 16px', backgroundColor:'#fff3f3', borderRadius:'10px', marginBottom:'15px', border:'1px solid #ffd6d6' },
    success: { color:'#155724', padding:'12px 16px', backgroundColor:'#d4edda', borderRadius:'10px', marginBottom:'15px' },
    addFormCard: { backgroundColor:'white', padding:'22px', borderRadius:'14px', marginBottom:'22px', boxShadow:'0 2px 8px rgba(0,0,0,0.06)', border:'2px solid #1F3864' },
    formTitle: { color:'#1F3864', margin:'0 0 15px 0', fontWeight:700 },
    inlineForm: {},
    formGrid: { display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', gap:'12px', marginBottom:'12px' },
    formGroup: { display:'flex', flexDirection:'column', gap:'5px' },
    label: { fontSize:'12px', fontWeight:'bold', color:'#1F3864' },
    input: { padding:'10px', borderRadius:'8px', border:'1.5px solid #ddd', fontSize:'14px' },
    btnGroup: { display:'flex', gap:'10px' },
    submitBtn: { backgroundColor:'#2E75B6', color:'white', border:'none', padding:'11px 22px', borderRadius:'10px', cursor:'pointer', fontWeight:'bold', fontSize:'14px' },
    cancelBtn: { backgroundColor:'#6c757d', color:'white', border:'none', padding:'11px 18px', borderRadius:'10px', cursor:'pointer', fontSize:'14px' },
    yearBlock: { marginBottom:'25px' },
    yearHeader: { backgroundColor:'#1F3864', padding:'14px 22px', borderRadius:'14px 14px 0 0', display:'flex', justifyContent:'space-between', alignItems:'center' },
    yearTitle: { color:'white', fontWeight:800, fontSize:'18px' },
    termsGrid: { display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))', gap:'15px', padding:'18px', backgroundColor:'white', borderRadius:'0 0 14px 14px', boxShadow:'0 2px 8px rgba(0,0,0,0.06)' },
    termCard: { backgroundColor:'#f8f9fa', borderRadius:'12px', padding:'16px', border:'1px solid #eee', transition:'transform 0.2s ease, box-shadow 0.2s ease' },
    modalOverlay: { position:'fixed', top:0, left:0, right:0, bottom:0, backgroundColor:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' },
    modal: { backgroundColor:'white', borderRadius:'16px', width:'100%', maxWidth:'700px', maxHeight:'90vh', display:'flex', flexDirection:'column', boxShadow:'0 20px 60px rgba(0,0,0,0.3)' },
    modalHeader: { backgroundColor:'#1F3864', padding:'20px 25px', borderRadius:'16px 16px 0 0', display:'flex', justifyContent:'space-between', alignItems:'center' },
    modalTitle: { color:'white', margin:0, fontSize:'18px', fontWeight:700 },
    modalClose: { background:'none', border:'none', color:'white', fontSize:'20px', cursor:'pointer' },
    modalSubtitle: { color:'#666', fontSize:'13px', padding:'15px 25px 0', margin:0, lineHeight:'1.5' },
    promotionTable: { flex:1, overflow:'auto', margin:'15px 0' },
    promotionHeader: { display:'grid', gridTemplateColumns:'1fr 30px 1fr 80px', gap:'10px', padding:'8px 25px', backgroundColor:'#f8f9fa', borderBottom:'2px solid #ddd', fontWeight:'bold', fontSize:'12px', color:'#1F3864' },
    emptyState: { backgroundColor:'white', padding:'60px', borderRadius:'14px', textAlign:'center', boxShadow:'0 2px 8px rgba(0,0,0,0.06)' },
};

export default AcademicYears;